"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { WorkflowAction } from "@/configuration/document-mappings/types";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { ApprovalEvidenceCard } from "@/src/components/tasks/approval-evidence-card";
import { OrderMatchPanelView } from "@/src/components/tasks/order-match-panel";
import { GlobalAlertInstructions, TeDecisionFlow } from "@/src/components/tasks/pa-east-workflow-action-content";
import { ReferralMatchPanelView } from "@/src/components/tasks/referral-match-panel";
import { InstructionField, ProcessedInstructions, useProcessedInstructions } from "@/src/components/tasks/use-processed-instructions";
import { cn } from "@/src/lib/shadcn-utils";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";
import { type TaskApproval } from "@/src/models/task-approval";

// A leading tab on an `instructions` config string marks it as a sub-item of the preceding line.
const NESTED_INSTRUCTION_MARKER = "\t";
const COPYABLE_PREFIXES = ["Document label:", "Upload to folder:", "Assign to:", "Description:", "Document type:", "Workflow Type:", "MOCK CHART:", "Reason:", "Assigned:", "Upload the Document to Patient and assign to ", "AHI: ", "Summary Statement:", "Recommendation:", "Doc Label:", "Document Type:", "ECW Folder :", "Scanned By:", "Transfer Reason:", "Edit Global Alert:", "Signature Page:", "Incomplete Reason:", "Task Type:", "Task Details:", "Internal Note:", "Task Note:", "Chart Note Subject:", "Chart Note Entry:", "Patient Name:", "DOB:", "Document Code:", "User:", "Body Part:", "Date:"];
function InstructionFieldRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <strong>{label}:</strong>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <CopyableContent text={value} />;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <p className="whitespace-pre-line">{t1} {t2}</p>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function processCopyableInstruction(instruction: string, index: number, processInstructionText: (text: string) => string) {
  const isNested = instruction.startsWith(NESTED_INSTRUCTION_MARKER);
  const nestedClassName = isNested ? "ml-6" : undefined;
  const renderedInstruction = processInstructionText(isNested ? instruction.slice(NESTED_INSTRUCTION_MARKER.length) : instruction);
  const matchedPrefix = COPYABLE_PREFIXES.find(prefix => renderedInstruction.startsWith(prefix));
  if (matchedPrefix) {
    let labelValue = renderedInstruction.substring(matchedPrefix.length).trim();

    // Special handling for MOCK CHART to exclude account number
    if (matchedPrefix === "MOCK CHART:") {
      labelValue = labelValue.replace(/\s*\(Acct\s*#\d+\)\s*$/, "");
    }
    return <li key={index} className={cn(nestedClassName, matchedPrefix === "Description:" && "whitespace-pre-wrap")}>
        {matchedPrefix} <CopyableContent text={labelValue} />
      </li>;
  }
  return <li key={index} className={nestedClassName}>
      {renderedInstruction}
    </li>;
}
function InstructionExtraFieldRow(t0) {
  const $ = _c(5);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <CopyableContent text={value} />;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <li>{label}: {t1}</li>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function renderCustomInstructionList(instructions: readonly string[], extraFields: InstructionField[], processInstructionText: (text: string) => string) {
  const staffLocationField = extraFields.find(field => field.label === "Staff Location");
  const otherExtraFields = extraFields.filter(field => field.label !== "Staff Location");
  let staffLocationInserted = false;
  const instructionItems = instructions.filter(instruction => instruction.trim() !== "").flatMap((instruction, index) => {
    const renderedInstruction = processInstructionText(instruction);
    const insertStaffLocationAfter = renderedInstruction.startsWith("Assign to:") && staffLocationField != null && !staffLocationInserted;
    if (insertStaffLocationAfter) {
      staffLocationInserted = true;
    }
    const items = [processCopyableInstruction(instruction, index, processInstructionText)];
    if (insertStaffLocationAfter) {
      items.push(<InstructionExtraFieldRow key="staff-location" label={staffLocationField.label} value={staffLocationField.value} />);
    }
    return items;
  });
  return [...instructionItems, ...otherExtraFields.map(field => <InstructionExtraFieldRow key={field.label} {...field} />)];
}
export function InstructionsStateCard(t0) {
  const $ = _c(3);
  const {
    message
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CardHeader><CardTitle className="text-center">Document Management Instructions</CardTitle></CardHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== message) {
    t2 = <Card className="bg-green-500/5 border border-green-500/20">{t1}<CardContent><p>{message}</p></CardContent></Card>;
    $[1] = message;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function DocumentInstructionsCardView(t0) {
  const $ = _c(32);
  const {
    data
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CardHeader><CardTitle className="text-center">Document Management Instructions</CardTitle></CardHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== data.displayName) {
    t2 = <p><strong>Document Management Instructions - {data.displayName}</strong></p>;
    $[1] = data.displayName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== data) {
    t3 = !data.instructions && <div className="mb-2"><p><strong>Instructions:</strong></p><div className="pl-4">{data.standardFields.map(_temp)}{data.extraFields.map(field_0 => <React.Fragment key={field_0.label}><InstructionFieldRow {...field_0} />{field_0.label === "Fax sent to" && data.additionalWorkflow != null && data.additionalWorkflow.length > 0 && data.additionalWorkflow.map((instruction, index) => <p key={index} className="whitespace-pre-line"><strong>Additional Workflow:</strong>{" "}<CopyableContent text={data.processInstructionText(instruction)} /></p>)}</React.Fragment>)}</div></div>;
    $[3] = data;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data) {
    t4 = data.instructions && <><p><strong>Instructions:</strong></p>{data.standardFields.filter(_temp2).map(_temp3)}{data.statusLine && <p><strong>{data.processInstructionText(data.statusLine)}</strong></p>}<ul className="list-disc pl-6 space-y-1">{renderCustomInstructionList(data.instructions, data.extraFields, data.processInstructionText)}</ul></>;
    $[5] = data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== data) {
    t5 = data.note && <><p><strong>{data.noteLabel ?? "Note"}:</strong></p><p className="pl-4 whitespace-pre-line">{data.processInstructionText(data.note)}</p></>;
    $[7] = data;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data.workflowAction) {
    t6 = data.workflowAction === WorkflowAction.TE && <TeDecisionFlow />;
    $[9] = data.workflowAction;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== data) {
    t7 = data.additionalWorkflow && Array.isArray(data.additionalWorkflow) && !data.extraFields.some(_temp4) && <div className="mb-2"><p><strong>Additional Workflow:</strong></p><div className="pl-4">{data.additionalWorkflow.map((instruction_0, index_0) => {
          const rendered = data.processInstructionText(instruction_0);
          const hasGlobalAlert = /global alert/i.test(rendered);
          return <React.Fragment key={index_0}>{processCopyableInstruction(instruction_0, index_0, data.processInstructionText)}{hasGlobalAlert && <GlobalAlertInstructions />}</React.Fragment>;
        })}</div></div>;
    $[11] = data;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== data.tags) {
    t8 = data.tags && data.tags.length > 0 && <div className="flex items-center gap-1.5 flex-wrap"><strong>Tags:</strong>{data.tags.map(_temp5)}</div>;
    $[13] = data.tags;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== data) {
    t9 = data.ccDescription && !data.instructions?.some(instruction_1 => data.processInstructionText(instruction_1).startsWith("Description:")) && <div className="mt-2"><p><strong>Description (will be filed with document):</strong></p><pre className="pl-4 whitespace-pre-wrap font-sans"><CopyableContent text={data.ccDescription} /></pre></div>;
    $[15] = data;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = data.stepOutputs?.order_match;
  let t11;
  if ($[17] !== t10) {
    t11 = <OrderMatchPanelView match={t10} />;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = data.stepOutputs?.referral_match;
  let t13;
  if ($[19] !== t12) {
    t13 = <ReferralMatchPanelView match={t12} />;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13 || $[23] !== t2 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t14 = <Card className="bg-green-500/5 border border-green-500/20">{t1}<CardContent><div className="flex flex-col gap-2 text-sm">{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t11}{t13}</div></CardContent></Card>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}
function _temp5(tag) {
  return <Badge key={tag} variant="secondary">{tag}</Badge>;
}
function _temp4(f) {
  return f.label === "Fax sent to";
}
function _temp3(field_2) {
  return <InstructionFieldRow key={field_2.label} {...field_2} />;
}
function _temp2(field_1) {
  return field_1.label === "Assign to";
}
function _temp(field) {
  return <InstructionFieldRow key={field.label} {...field} />;
}
export function DocumentManagementInstructions(t0) {
  const $ = _c(6);
  const {
    task,
    section,
    pendingApproval
  } = t0;
  const {
    data,
    isLoading,
    isError
  } = useProcessedInstructions(task, section, !pendingApproval);
  if (pendingApproval) {
    let t1;
    if ($[0] !== pendingApproval) {
      t1 = <ApprovalEvidenceCard approval={pendingApproval} />;
      $[0] = pendingApproval;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (isLoading) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <InstructionsStateCard message="Loading instructions..." />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (isError || !data) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <InstructionsStateCard message="Error loading instructions or no instructions found." />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== data) {
    t1 = <DocumentInstructionsCardView data={data} />;
    $[4] = data;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}